/**
 * Command palette. MASTER_PROMPT section 7.4 — the single most "AI tool"
 * affordance, and the fastest route to anything for a keyboard user.
 *
 * It does two things. Typing filters the screens this account may open;
 * pressing Enter on something that matches nothing sends the text to the
 * natural-language query layer (section 11, capability 2), which runs
 * read-only against allow-listed views and cannot change anything. The
 * generated SQL is shown alongside the answer, because an answer whose
 * derivation is hidden is not evidence.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError } from "../../lib/api";
import type { AskResult } from "../../lib/types";
import type { Route } from "./AppShell";

interface Command {
  id: string;
  label: string;
  hint: string;
  group: string;
  route?: Route;
}

const COMMANDS: Command[] = [
  { id: "executive", label: "Control tower", hint: "Plant KPIs and the action list", group: "Go to", route: "executive" },
  { id: "entry", label: "Shift entry", hint: "Record a machine shift", group: "Go to", route: "entry" },
  { id: "looms", label: "Loom floor", hint: "Ninety looms by efficiency", group: "Go to", route: "looms" },
  { id: "entries", label: "Entries", hint: "See, edit or delete a shift already entered", group: "Go to", route: "entries" },
  { id: "production", label: "Production report", hint: "Hours, manpower, power and material for any period", group: "Go to", route: "production" },
  { id: "efficiency", label: "Machine efficiency", hint: "Production per day against capacity: tape, RP, loom, dispatch", group: "Go to", route: "efficiency" },
  { id: "exceptions", label: "Exceptions", hint: "Negative stock, tolerance breaches and changed entries", group: "Go to", route: "exceptions" },
  { id: "variance", label: "Variance queue", hint: "Verify submitted shifts", group: "Go to", route: "variance" },
  { id: "purchase", label: "Goods receipt", hint: "Receive scrap or raw material", group: "Go to", route: "purchase" },
  { id: "stock", label: "Stock", hint: "Balances at any location, any date", group: "Go to", route: "stock" },
  { id: "recycling", label: "Recycling line", hint: "RP yield, input mix, where the kilograms go", group: "Go to", route: "recycling" },
  { id: "ink", label: "Printing & ink", hint: "Cost per bag and the reducer band", group: "Go to", route: "ink" },
  { id: "dispatchboard", label: "Dispatch board", hint: "Weighbridge gaps and who they went to", group: "Go to", route: "dispatchboard" },
  { id: "wip", label: "WIP ladder", hint: "Five states per SKU, from the ledger", group: "Go to", route: "wip" },
  { id: "dispatch", label: "Dispatch", hint: "Raise, weigh and close a consignment", group: "Go to", route: "dispatch" },
  { id: "transfers", label: "Transfers", hint: "Inter-unit movements and transit ageing", group: "Go to", route: "transfers" },
  { id: "counts", label: "Stock counts", hint: "Quarterly and cycle counts", group: "Go to", route: "counts" },
  { id: "tally", label: "Tally", hint: "Voucher batches, Excel and XML", group: "Go to", route: "tally" },
  { id: "costing", label: "Costing", hint: "Absorption cost run and drivers", group: "Go to", route: "costing" },
  { id: "intake", label: "Data intake", hint: "Power, labour and rate templates", group: "Go to", route: "intake" },
  { id: "close", label: "Period close", hint: "The month-end checklist and lock", group: "Go to", route: "close" },
  { id: "settings", label: "Settings", hint: "Demo data, and the rest of the administration", group: "Go to", route: "settings" },
];

interface Props {
  /** The routes this account may open. The palette never offers others. */
  routes: Route[];
  onClose: () => void;
  onNavigate: (route: Route) => void;
}

export function CommandPalette({ routes, onClose, onNavigate }: Props) {
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState<AskResult | null>(null);
  const [askError, setAskError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Only commands whose screen is in this account's rail.
  const available = useMemo(
    () => COMMANDS.filter((c) => !c.route || routes.includes(c.route)),
    [routes]
  );

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return available;
    return available.filter(
      (c) =>
        c.label.toLowerCase().includes(q) || c.hint.toLowerCase().includes(q)
    );
  }, [query, available]);

  useEffect(() => {
    setCursor(0);
    setAnswer(null);
    setAskError(null);
  }, [query]);

  async function ask() {
    const question = query.trim();
    if (question.length < 3 || asking) return;
    setAsking(true);
    setAskError(null);
    try {
      setAnswer(await api.ask(question));
    } catch (err) {
      setAskError((err as ApiError).message);
      setAnswer(null);
    } finally {
      setAsking(false);
    }
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setCursor((c) => Math.min(c + 1, matches.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      const chosen = matches[cursor];
      // A phrase that matches no screen is a question, not a mistyped
      // command — send it to the query layer rather than doing nothing.
      if (chosen?.route) onNavigate(chosen.route);
      else void ask();
    }
  }

  return (
    <div className="palette" role="dialog" aria-modal="true" aria-label="Commands">
      <button
        className="palette__scrim"
        onClick={onClose}
        aria-label="Close command palette"
        tabIndex={-1}
      />
      <div className="palette__panel glass">
        <input
          ref={inputRef}
          className="palette__input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search, or ask a question…"
          aria-label="Command"
        />

        {matches.length === 0 ? (
          <div className="palette__empty">
            {asking ? (
              <p>
                <span className="spinner" /> Asking the plant…
              </p>
            ) : answer ? (
              <>
                <p className="palette__muted">{answer.explanation}</p>
                {answer.rows.length === 0 ? (
                  <p>No rows matched that question.</p>
                ) : (
                  <div className="tablewrap">
                    <table className="table">
                      <thead>
                        <tr>
                          {answer.columns.map((c) => (
                            <th key={c}>{c.replace(/_/g, " ")}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {answer.rows.slice(0, 20).map((row, i) => (
                          <tr key={i}>
                            {answer.columns.map((c) => (
                              <td
                                key={c}
                                className={
                                  typeof row[c] === "number" ? "num mono" : ""
                                }
                              >
                                {row[c] === null || row[c] === undefined
                                  ? "—"
                                  : String(row[c])}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {/* The derivation, not just the number. An answer whose
                    query is hidden cannot be checked. */}
                <pre className="palette__sql mono">{answer.sql}</pre>
              </>
            ) : askError ? (
              <>
                <p>{askError}</p>
                <p className="palette__muted">
                  Try asking about production, stock, dispatch, looms or
                  contractor output.
                </p>
              </>
            ) : (
              <>
                <p>Nothing matches &ldquo;{query}&rdquo;.</p>
                <p className="palette__muted">
                  Press <kbd>↵</kbd> to ask the plant instead. Questions run
                  read-only against the reporting views and cannot change
                  anything.
                </p>
              </>
            )}
          </div>
        ) : (
          <ul className="palette__list">
            {matches.map((command, index) => (
              <li key={command.id}>
                <button
                  className={`palette__item${
                    index === cursor ? " palette__item--active" : ""
                  }`}
                  onMouseEnter={() => setCursor(index)}
                  onClick={() => command.route && onNavigate(command.route)}
                >
                  <span className="palette__group">{command.group}</span>
                  <span className="palette__label">{command.label}</span>
                  <span className="palette__hint">{command.hint}</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <footer className="palette__footer">
          <kbd>↑</kbd>
          <kbd>↓</kbd> navigate <kbd>↵</kbd>{" "}
          {matches.length ? "open" : "ask"} <kbd>esc</kbd> close
        </footer>
      </div>
    </div>
  );
}
